// Default is SAME-ORIGIN (empty prefix): every /api/* call hits the Next
// server itself, which proxies to the FastAPI backend (see next.config.ts
// rewrites). That makes the app host-agnostic — localhost, LAN IP, or the
// public cloudflared tunnel all work with cookies intact and zero CORS.
// NEXT_PUBLIC_API_URL remains as an escape hatch to point the browser at a
// backend on a different origin directly.
const API_URL = process.env.NEXT_PUBLIC_API_URL || "";

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    ...init,
    credentials: "include",
    headers: {
      ...(init?.body && !(init.body instanceof FormData)
        ? { "Content-Type": "application/json" }
        : {}),
      ...init?.headers,
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, body.detail || res.statusText);
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

// fetch() has no upload-progress event — only XHR exposes
// `upload.onprogress`, which is what a multi-hundred-MB video import needs
// so it doesn't look like the page silently hung for a minute-plus.
function requestWithProgress<T>(
  path: string,
  form: FormData,
  onProgress?: (fraction: number) => void,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${API_URL}${path}`);
    xhr.withCredentials = true;
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress?.(e.loaded / e.total);
    };
    xhr.onload = () => {
      let body: unknown = undefined;
      try {
        body = xhr.responseText ? JSON.parse(xhr.responseText) : undefined;
      } catch {
        // non-JSON response (e.g. a proxy-level error page) — fall through
        // to the status-based ApiError below with the raw text as message.
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(body as T);
      } else {
        const detail =
          body && typeof body === "object" && "detail" in body
            ? String((body as { detail: unknown }).detail)
            : xhr.responseText || xhr.statusText;
        reject(new ApiError(xhr.status, detail));
      }
    };
    xhr.onerror = () => reject(new ApiError(0, "Network error during upload"));
    xhr.send(form);
  });
}

export interface User {
  id: string;
  email: string;
  name: string | null;
  is_admin: boolean;
  credits: number;
}

/** One row of the admin user table. Usage numbers come from real projects,
 *  jobs and the credit ledger — see routes.py's /admin/users. */
export interface AdminUser {
  id: string;
  email: string;
  name: string | null;
  is_admin: boolean;
  credits: number;
  created_at: string;
  projects_count: number;
  jobs_count: number;
  credits_spent: number;
  credits_granted: number;
  last_job_at: string | null;
}

/** One plugin trial request, with how often its emailed link was used. */
export interface AdminPluginTrial {
  id: string;
  email: string;
  product: string;
  platform: string;
  ip: string | null;
  created_at: string;
  downloads_count: number;
  last_download_at: string | null;
}

export type ProjectKind = "dub" | "voice" | "script";

/** Everything ffmpeg/libass needs to draw a styled subtitle — the SAME
 * shape the backend stores on the project (schemas.SubtitleStyle) and burns
 * with. lib/subtitle-style.ts maps it to CSS for the panel preview and the
 * live player overlay, so all three surfaces show one look.
 * `alignment` is the ASS numpad convention (1-9 = bottom/middle/top row x
 * left/center/right column; 2 = bottom-center). */
export interface SubtitleStyle {
  font_family: string;
  font_size: number;
  primary_color: string;
  outline_color: string;
  outline_width: number;
  shadow: number;
  background_box: boolean;
  background_color: string;
  background_opacity: number;
  alignment: number;
  margin_v: number;
  margin_h: number;
}

/** Defaults chosen to reproduce the look the burn had while it was
 * hardcoded, so an existing project's export does not change appearance
 * just because styling became editable. */
export const DEFAULT_SUBTITLE_STYLE: SubtitleStyle = {
  font_family: "DejaVu Sans",
  font_size: 24,
  primary_color: "#FFFFFF",
  outline_color: "#000000",
  outline_width: 2,
  shadow: 0,
  background_box: false,
  background_color: "#000000",
  background_opacity: 0.6,
  alignment: 2,
  margin_v: 28,
  margin_h: 20,
};

/** A saved look, owned by the ACCOUNT so a house style outlives the project
 * it was tuned on. Capped at 5 server-side. */
export interface SubtitlePreset {
  id: string;
  name: string;
  style: SubtitleStyle;
  created_at: string;
}

export interface Project {
  id: string;
  name: string;
  /** Which workspace this project opens in — see components/README.md. */
  kind: ProjectKind;
  status: string;
  original_audio_path: string | null;
  cleaned_audio_path: string | null;
  original_video_path: string | null;
  preview_video_path: string | null;
  total_duration_s: number | null;
  created_at: string;

  target_language: string;
  gen_mode: "clone" | "tts";
  gender: "male" | "female";
  voice_description: string;
  engine_text: string;
  shot_mode: "closeup" | "wide";
  vc_steps: number;
  vc_guidance: number;
  silence_threshold_db: number;
  validate_enabled: boolean;
  ref_mode: "global" | "block";
  global_ref_path: string | null;

  merge_mode: "clean" | "overlay" | "mix";
  final_audio_path: string | null;
  final_video_path: string | null;
  /** Source video with the subtitles burned in — the Transcribe
   * workspace's deliverable. Null until a burn-subtitles job has run. */
  subtitled_video_path: string | null;
  /** The one stored subtitle look — read by the panel's frame preview, the
   * live player overlay AND the ffmpeg burn, so they cannot disagree. Null
   * on projects predating styling; fall back to DEFAULT_SUBTITLE_STYLE. */
  subtitle_style: SubtitleStyle | null;
  /** Spans of the source that get transcribed at all — empty/null means
   * the whole file. Set by dragging on the Step 1 waveform. */
  transcribe_regions: { start: number; end: number }[] | null;
  /** TTS mode: the approved 15s sample every take is converted onto, so
   * the voice you picked survives onto the real script. */
  tts_voice_anchor_path: string | null;
  tts_voice_seed: number | null;
  /** Checkerboard stems — odd blocks render to stem A, even to stem B (both
   * dub-on-silence), so adjacent blocks can never overlap within one file. */
  merge_split_stems: boolean;
  final_stem_a_path: string | null;
  final_stem_b_path: string | null;
  /** What the LAST completed merge actually ran with — written by the merge
   * worker, never by the settings endpoint. Step 3 diffs these against
   * merge_mode / merge_split_stems to warn that the files you can download
   * are from the previous settings. Null on projects merged before these
   * columns existed (unknown, so treated as not stale). */
  merged_with_mode: "clean" | "overlay" | "mix" | null;
  merged_with_split_stems: boolean | null;

  /** How the CURRENT viewer relates to this project — "owner" for own
   * projects; shared ones carry their share level. Read-only UIs key off
   * access === "read". */
  access: "owner" | "read" | "full";
  /** Set only on shared projects — the owning account's email, for the
   * "shared by …" byline. */
  owner_email: string | null;
  /** Owner-perspective only — how many accounts this project is shared
   * with. Always 0 on a project shared WITH you (that side shows its own
   * access-level badge instead). Powers the "Shared" badge on the list. */
  share_count: number;
  /** Jobs run by OTHER users since you last opened this project — drives
   * the notification dot on the projects page (0 = no dot). */
  unseen_events: number;
}

export interface ProjectShare {
  id: string;
  email: string;
  access: "read" | "full";
  created_at: string;
}

/** One row of the project activity log (a job + who triggered it). */
export interface ProjectEvent {
  id: string;
  type: string;
  status: string;
  created_at: string | null;
  user_email: string | null;
  block_idx: number | null;
  method: string | null;
  error: string | null;
  /** Run by someone ELSE after your last visit to this project. */
  new: boolean;
}

export interface ProjectSettingsUpdate {
  target_language?: string;
  gen_mode?: "clone" | "tts";
  gender?: "male" | "female";
  voice_description?: string;
  engine_text?: string;
  shot_mode?: "closeup" | "wide";
  vc_steps?: number;
  vc_guidance?: number;
  silence_threshold_db?: number;
  validate_enabled?: boolean;
  ref_mode?: "global" | "block";
  merge_mode?: "clean" | "overlay" | "mix";
  merge_split_stems?: boolean;
  transcribe_regions?: { start: number; end: number }[];
  subtitle_style?: SubtitleStyle;
  total_duration_s?: number;
}

export interface Lane {
  id: string;
  idx: number;
  name: string;
  muted: boolean;
  solo: boolean;
}

export interface LaneClip {
  id: string;
  lane_idx: number;
  name: string;
  file_path: string;
  start_s: number;
  duration_s: number;
}

export interface GlobalRef {
  id: string;
  name: string;
  file_path: string;
  duration_s: number;
  source_block_ids: string[] | null;
}

export interface Block {
  id: string;
  idx: number;
  start: number;
  end: number;
  /** Where this block's generated dub lands on the Step 3 merge timeline —
   * null means "use start" (unmoved). Set via Step 3's Arrange panel. */
  merge_offset_s: number | null;
  /** Which Step 3 arrange lane this take is routed to (0 = original, never a take). */
  lane_idx: number;
  orig_text: string;
  trans_text: string;
  prompt: string;
  orig_audio_path: string | null;
  gen_audio_path: string | null;

  seed: number;
  seed_lock: boolean;
  pace: number;
  alt_ref_block_id: string | null;
  /** Every alt-ref pick, in order — only consulted when combine_ref is on,
   * where they're all concatenated into ONE cloning reference (capped at
   * SeedVC's 25s reference limit). alt_ref_block_id mirrors entry 0, so
   * single-ref behavior is unchanged. */
  alt_ref_block_ids: string[] | null;
  combine_ref: boolean;
  global_ref_id: string | null;
  /** Per-segment reference mode. null = follow the project's ref_mode. */
  ref_mode: "global" | "block" | null;
  /** Pause-sync ON per block by default. OFF generates the line as one
   * continuous take (no phrase-split / pause tags / stage-2 shift). */
  pause_sync_enabled: boolean;
  hint: string;
  gen_version: number;
  metadata_json: Record<string, unknown>;
}

export interface BlockUpdate {
  orig_text?: string;
  trans_text?: string;
  prompt?: string;
  seed?: number;
  seed_lock?: boolean;
  pace?: number;
  alt_ref_block_id?: string | null;
  alt_ref_block_ids?: string[] | null;
  combine_ref?: boolean;
  global_ref_id?: string | null;
  ref_mode?: "global" | "block" | null;
  pause_sync_enabled?: boolean;
  hint?: string;
  /** Step 3 arrange lane (1-5; lane 0 is the original and refuses takes). */
  lane_idx?: number;
}

export interface AlignClip {
  start: number;
  end: number;
  offset: number;
}

export interface Job {
  id: string;
  project_id: string;
  block_id: string | null;
  type: string;
  status: "queued" | "running" | "done" | "failed" | "cancelled";
  queue_position: number | null;
  progress: number | null;
  cancel_requested: boolean;
  result_json: Record<string, unknown> | null;
  error: string | null;
}

export interface WaveformData {
  peaks: number[];
  duration: number;
}

/** BBC waveform-data JSON schema — matches PeaksJsonWaveformData in
 * components/block-waveform.tsx; kept as a separate type here since api.ts
 * shouldn't import from a component file. */
export interface PeaksJsonWaveformData {
  version: number;
  channels: number;
  sample_rate: number;
  samples_per_pixel: number;
  bits: number;
  length: number;
  data: number[];
}

export interface BlockPeaksWaveform {
  waveform: PeaksJsonWaveformData | null;
  duration: number;
}

export interface BlockVersion {
  id: string;
  version: number;
  seed: number;
  pace: number;
  file_path: string;
  metadata_json: Record<string, unknown>;
  created_at: string;
  /** Locked against the MAX_VERSIONS_ON_DISK prune — the padlock next to a
   * generated take's download button. Max MAX_PINNED_VERSIONS (3) per block. */
  pinned: boolean;
  /** False once disk pruning (worker.py's MAX_VERSIONS_ON_DISK) has deleted
   * this take's file — the row (real history) still exists, there's just
   * nothing left to play. */
  exists: boolean;
}

export const api = {
  login: (email: string, password: string) =>
    request<User>("/api/auth/login", { method: "POST", body: JSON.stringify({ email, password }) }),
  register: (email: string, password: string, name?: string) =>
    request<User>("/api/auth/register", { method: "POST", body: JSON.stringify({ email, password, name }) }),
  logout: () => request<{ status: string }>("/api/auth/logout", { method: "POST" }),
  me: () => request<User>("/api/auth/me"),
  updateMe: (body: { name?: string; current_password?: string; new_password?: string }) =>
    request<User>("/api/auth/me", { method: "PATCH", body: JSON.stringify(body) }),
  contact: (name: string, email: string, message: string) =>
    request<void>("/api/contact", { method: "POST", body: JSON.stringify({ name, email, message }) }),
  /** Irreversible. Deletes every project and file the account owns, then the
   *  account itself; the password is required so a borrowed session cannot
   *  do it alone. */
  deleteAccount: (password: string) =>
    request<{ status: string; projects_deleted: number }>("/api/auth/me/delete", {
      method: "POST",
      body: JSON.stringify({ password }),
    }),

  // Admin-only. The backend answers 404 (not 403) to non-admins, so these
  // simply behave as if the surface does not exist.
  adminUsers: () => request<AdminUser[]>("/api/admin/users"),
  adminPluginTrials: () => request<AdminPluginTrial[]>("/api/admin/plugin-trials"),
  /** Asks the backend to email a personal installer link. `website` is the
   *  honeypot the form hides from people; it is sent so a bot that filled
   *  it can be told apart on the server, never on the client. */
  requestPluginTrial: (email: string, product: string, platform: string, website: string) =>
    request<void>("/api/plugins/trial", {
      method: "POST",
      body: JSON.stringify({ email, product, platform, website }),
    }),
  adminGrantCredits: (userId: string, amount: number, note?: string) =>
    request<AdminUser>(`/api/admin/users/${userId}/credits`, {
      method: "POST",
      body: JSON.stringify({ amount, note }),
    }),
  getStats: () =>
    request<{
      projects_created: number;
      blocks_generated: number;
      languages_used: number;
      total_dubbed_seconds: number;
    }>("/api/me/stats"),
  getActivity: (limit = 10) =>
    request<{ date: string; action: string; project: string }[]>(`/api/me/activity?limit=${limit}`),

  listProjects: () => request<Project[]>("/api/projects"),
  createProject: (name: string, kind: ProjectKind = "dub") =>
    request<Project>("/api/projects", { method: "POST", body: JSON.stringify({ name, kind }) }),
  getProject: (id: string) => request<Project>(`/api/projects/${id}`),
  deleteProject: (id: string) => request<void>(`/api/projects/${id}`, { method: "DELETE" }),
  getProjectWaveform: (id: string, which: "orig" | "final" = "orig") =>
    request<WaveformData>(`/api/projects/${id}/waveform?which=${which}`),
  getProjectPeaksWaveform: (id: string, which: "orig" | "final" = "orig") =>
    request<BlockPeaksWaveform>(`/api/projects/${id}/waveform?which=${which}&format=peaksjs`),
  updateSettings: (id: string, body: ProjectSettingsUpdate) =>
    request<Project>(`/api/projects/${id}/settings`, { method: "PATCH", body: JSON.stringify(body) }),
  uploadGlobalRef: (id: string, file: File) => {
    const form = new FormData();
    form.append("file", file);
    return request<Project>(`/api/projects/${id}/settings/global_ref`, { method: "POST", body: form });
  },
  uploadAudio: (id: string, file: File, onProgress?: (fraction: number) => void) => {
    const form = new FormData();
    form.append("file", file);
    return requestWithProgress<Project>(`/api/projects/${id}/audio`, form, onProgress);
  },
  importVideo: (id: string, file: File, onProgress?: (fraction: number) => void) => {
    const form = new FormData();
    form.append("file", file);
    return requestWithProgress<Project>(`/api/projects/${id}/audio?target=video`, form, onProgress);
  },
  importAudioOnly: (id: string, file: File, onProgress?: (fraction: number) => void) => {
    const form = new FormData();
    form.append("file", file);
    return requestWithProgress<Project>(`/api/projects/${id}/audio?target=audio`, form, onProgress);
  },
  deleteVideo: (id: string) => request<Project>(`/api/projects/${id}/video`, { method: "DELETE" }),
  deleteAudio: (id: string) => request<Project>(`/api/projects/${id}/audio`, { method: "DELETE" }),

  listShares: (projectId: string) => request<ProjectShare[]>(`/api/projects/${projectId}/shares`),
  getProjectHistory: (projectId: string) => request<ProjectEvent[]>(`/api/projects/${projectId}/history`),
  // Explicit acknowledgement — clears the "changes since your last visit"
  // dot. Separate from the GET so refetches can't silently eat the flags.
  markHistorySeen: (projectId: string) =>
    request<void>(`/api/projects/${projectId}/history/seen`, { method: "POST" }),
  createShare: (projectId: string, email: string, access: "read" | "full") =>
    request<ProjectShare[]>(`/api/projects/${projectId}/shares`, {
      method: "POST",
      body: JSON.stringify({ email, access }),
    }),
  deleteShare: (projectId: string, shareId: string) =>
    request<ProjectShare[]>(`/api/projects/${projectId}/shares/${shareId}`, { method: "DELETE" }),

  /** Pre-flight cost estimate for a confirmation dialog before an
   * expensive/long-running job — the SAME estimator the real reserve step
   * uses server-side, so the number shown is never a lie. */
  estimateCost: (
    projectId: string,
    params: {
      job_type: "transcribe" | "translate_all" | "generate" | "resegment";
      block_count?: number;
      translation_method?: string;
      block_idx?: number;
    },
  ) => {
    const q = new URLSearchParams({ job_type: params.job_type });
    if (params.block_count !== undefined) q.set("block_count", String(params.block_count));
    if (params.translation_method) q.set("translation_method", params.translation_method);
    if (params.block_idx !== undefined) q.set("block_idx", String(params.block_idx));
    return request<{ credits: number; free: boolean }>(`/api/projects/${projectId}/estimate-cost?${q}`);
  },
  enqueueTranscribe: (
    id: string,
    body: {
      source_lang: string;
      min_dur?: number;
      max_dur?: number;
      clean_audio?: boolean;
    }
  ) => request<Job>(`/api/projects/${id}/jobs/transcribe`, { method: "POST", body: JSON.stringify(body) }),
  importText: (id: string, file: File) => {
    const form = new FormData();
    form.append("file", file);
    return request<Job>(`/api/projects/${id}/jobs/import-text`, { method: "POST", body: form });
  },
  getJob: (id: string) => request<Job>(`/api/jobs/${id}`),

  listBlocks: (projectId: string) => request<Block[]>(`/api/projects/${projectId}/blocks`),
  updateBlock: (projectId: string, idx: number, body: BlockUpdate) =>
    request<Block>(`/api/projects/${projectId}/blocks/${idx}`, { method: "PATCH", body: JSON.stringify(body) }),

  // Fire-and-forget variant for page-unload flushes: `keepalive` lets the
  // browser finish delivering the request AFTER the tab is gone — a normal
  // fetch started during pagehide gets aborted, which is exactly how a
  // still-debounced edit used to die with its tab.
  updateBlockKeepalive: (projectId: string, idx: number, body: BlockUpdate) => {
    void fetch(`${API_URL}/api/projects/${projectId}/blocks/${idx}`, {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => {})
  },
  addBlock: (projectId: string) =>
    request<Block[]>(`/api/projects/${projectId}/blocks`, { method: "POST" }),
  deleteBlock: (projectId: string, idx: number) =>
    request<Block[]>(`/api/projects/${projectId}/blocks/${idx}`, { method: "DELETE" }),
  splitBlock: (projectId: string, idx: number, cursorFraction?: number) =>
    request<Block[]>(`/api/projects/${projectId}/blocks/${idx}/split`, {
      method: "POST",
      body: JSON.stringify({ cursor_fraction: cursorFraction ?? null }),
    }),
  mergeBlock: (projectId: string, idx: number) =>
    request<Block[]>(`/api/projects/${projectId}/blocks/${idx}/merge`, { method: "POST" }),
  // TTS: splice the block's shown take into the master voiceover at the
  // block's window; the server shifts every later window and returns the
  // fresh block list.
  // Global Refs (HANDOFF_GLOBAL_REFS.md): a named library of cloning
  // references per project; a block pointing at one clones from
  // hybrid(ref body, own crop tail).
  listGlobalRefs: (projectId: string) =>
    request<GlobalRef[]>(`/api/projects/${projectId}/global-refs`),
  createGlobalRef: (projectId: string, body: { block_ids: string[]; name?: string }) =>
    request<GlobalRef>(`/api/projects/${projectId}/global-refs`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  // Bring a voice in from outside the project (actor recording, client
  // sample, another film) — it lands in the same library as an ordinary
  // named ref, pickable per block.
  importGlobalRef: (projectId: string, file: File, name?: string) => {
    const form = new FormData();
    form.append("file", file);
    if (name) form.append("name", name);
    return request<GlobalRef>(`/api/projects/${projectId}/global-refs/import`, {
      method: "POST",
      body: form,
    });
  },
  renameGlobalRef: (projectId: string, refId: string, name: string) =>
    request<GlobalRef>(`/api/projects/${projectId}/global-refs/${refId}`, {
      method: "PATCH",
      body: JSON.stringify({ name }),
    }),
  deleteGlobalRef: (projectId: string, refId: string) =>
    request<GlobalRef[]>(`/api/projects/${projectId}/global-refs/${refId}`, {
      method: "DELETE",
    }),
  applyBlockToMaster: (projectId: string, idx: number, versionId?: string) =>
    request<Block[]>(`/api/projects/${projectId}/blocks/${idx}/apply-to-master`, {
      method: "POST",
      body: JSON.stringify({ version_id: versionId ?? null }),
    }),
  insertBlock: (projectId: string, idx: number, body: { start: number; end: number; orig_text: string }) =>
    request<Block[]>(`/api/projects/${projectId}/blocks/${idx}/insert`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  // Absolute span for one cue — the subtitle Arrange panel's write. Unlike
  // retimeBlock this never touches the neighbouring block (see the
  // set_block_timing docstring: subtitles float, dub blocks tile).
  setBlockTiming: (projectId: string, idx: number, start: number, end: number) =>
    request<Block[]>(`/api/projects/${projectId}/blocks/${idx}/timing`, {
      method: "POST",
      body: JSON.stringify({ start, end }),
    }),
  retimeBlock: (projectId: string, idx: number, edge: "start" | "end", time: number) =>
    request<Block[]>(`/api/projects/${projectId}/blocks/${idx}/retime`, {
      method: "POST",
      body: JSON.stringify({ edge, time }),
    }),
  // ── Step 3 arrange lanes ─────────────────────────────────────────────
  // Six DAW-style tracks per project. Lane 0 carries the original; mute/solo
  // decide what reaches the export (they replaced the old merge modes).
  listLanes: (projectId: string) => request<Lane[]>(`/api/projects/${projectId}/lanes`),
  updateLane: (projectId: string, idx: number, body: Partial<Pick<Lane, "name" | "muted" | "solo">>) =>
    request<Lane>(`/api/projects/${projectId}/lanes/${idx}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  listLaneClips: (projectId: string) => request<LaneClip[]>(`/api/projects/${projectId}/lane-clips`),
  uploadLaneClip: (projectId: string, laneIdx: number, file: File, startS = 0) => {
    const form = new FormData();
    form.append("file", file);
    form.append("start_s", String(startS));
    return request<LaneClip>(`/api/projects/${projectId}/lanes/${laneIdx}/clips`, {
      method: "POST",
      body: form,
    });
  },
  updateLaneClip: (
    projectId: string,
    clipId: string,
    body: { lane_idx?: number; start_s?: number; name?: string },
  ) =>
    request<LaneClip>(`/api/projects/${projectId}/lane-clips/${clipId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  deleteLaneClip: (projectId: string, clipId: string) =>
    request<LaneClip[]>(`/api/projects/${projectId}/lane-clips/${clipId}`, { method: "DELETE" }),
  /** Lanes that actually have a stem rendered from the last merge. */
  listStems: (projectId: string) => request<Lane[]>(`/api/projects/${projectId}/stems`),

  setBlockMergeOffset: (projectId: string, idx: number, offset: number | null) =>
    request<Block[]>(`/api/projects/${projectId}/blocks/${idx}/merge-offset`, {
      method: "POST",
      body: JSON.stringify({ offset }),
    }),
  generateBlock: (projectId: string, idx: number) =>
    request<Job>(`/api/projects/${projectId}/blocks/${idx}/generate`, { method: "POST" }),
  // jobType defaults to "generate" (Step 2's block-generation queue); Step 1
  // passes "translate_block" for its own on-air Stop (same backend route,
  // same query-param-scoped logic — see routes.py's job_type docstrings).
  cancelGeneration: (projectId: string, jobType: string = "generate") =>
    request<{ cancelled: number; stopping: number }>(`/api/projects/${projectId}/jobs/cancel-generation?job_type=${jobType}`, { method: "POST" }),
  generationStatus: (projectId: string, jobType: string = "generate") =>
    request<{ pending: number; running: number; block_ids: string[] }>(`/api/projects/${projectId}/jobs/generation-status?job_type=${jobType}`),
  activeJob: (projectId: string) =>
    request<Job | null>(`/api/projects/${projectId}/jobs/active`),
  getBlockWaveform: (projectId: string, idx: number, which: "orig" | "gen", versionId?: string) =>
    request<WaveformData>(
      `/api/projects/${projectId}/blocks/${idx}/waveform?which=${which}` +
        (versionId ? `&version_id=${encodeURIComponent(versionId)}` : ""),
    ),
  getBlockPeaksWaveform: (projectId: string, idx: number, which: "orig" | "gen", versionId?: string) =>
    request<BlockPeaksWaveform>(
      `/api/projects/${projectId}/blocks/${idx}/waveform?which=${which}&format=peaksjs` +
        (versionId ? `&version_id=${encodeURIComponent(versionId)}` : ""),
    ),
  getBlockVersions: (projectId: string, idx: number) =>
    request<BlockVersion[]>(`/api/projects/${projectId}/blocks/${idx}/versions`),
  pinBlockVersion: (projectId: string, idx: number, versionId: string, pinned: boolean) =>
    request<BlockVersion[]>(`/api/projects/${projectId}/blocks/${idx}/versions/${versionId}/pin`, {
      method: "POST",
      body: JSON.stringify({ pinned }),
    }),
  // Makes a PAST version the block's actual current take (gen_audio_path) —
  // the version stepper alone only changes what's DISPLAYED, never what
  // merge actually exports (see routes.py's restore_block_version docstring).
  restoreBlockVersion: (projectId: string, idx: number, versionId: string) =>
    request<Block>(`/api/projects/${projectId}/blocks/${idx}/versions/${versionId}/restore`, {
      method: "POST",
    }),
  downloadBlockGeneratedUrl: (projectId: string, idx: number, versionId?: string) =>
    `${API_URL}/api/projects/${projectId}/blocks/${idx}/download/generated` +
    (versionId ? `?version_id=${encodeURIComponent(versionId)}` : ""),
  alignBlock: (projectId: string, idx: number, clips: AlignClip[], versionId?: string) =>
    request<Block>(`/api/projects/${projectId}/blocks/${idx}/align`, {
      method: "POST",
      body: JSON.stringify({ clips, version_id: versionId ?? null }),
    }),

  // Additional Editor save (see project-additional-editor): splice a different
  // version's edited clips onto the block's base arrangement, on the SAME
  // coordinate timeline, into a new BlockVersion, crossfading where they meet.
  // baseVersionId + baseClips = the Main editor's LIVE (possibly-unsaved) clips
  // and the version they were cut from, so its edits are never lost by a splice
  // (baseClips empty -> the full take). layerVersionId + layerClips = the
  // Additional Editor's chosen version and its edited clips.
  spliceBlock: (
    projectId: string,
    idx: number,
    layerVersionId: string,
    layerClips: AlignClip[],
    baseVersionId: string | null,
    baseClips: AlignClip[],
  ) =>
    request<Block>(`/api/projects/${projectId}/blocks/${idx}/splice`, {
      method: "POST",
      body: JSON.stringify({
        layer_version_id: layerVersionId,
        layer_clips: layerClips,
        base_version_id: baseVersionId,
        base_clips: baseClips,
      }),
    }),

  // trims: EPHEMERAL Step-3 Arrange-panel edge trims (block id -> seconds off
  // head/tail), applied to THIS merge only and never persisted to the block.
  enqueueMerge: (
    projectId: string,
    trims: Record<string, { start: number; end: number }> = {},
  ) =>
    request<Job>(`/api/projects/${projectId}/jobs/merge`, {
      method: "POST",
      body: JSON.stringify({ trims }),
    }),

  // Re-splits every block's dub line to follow the original's phrasing (so
  // pauses line up before generation). A project-level batch job like
  // translateAll — same Job shape, polled the same way (0..1 progress).
  // blockIdx (optional): scope to a single block — the per-block "this
  // changed, resegment before generating?" confirm. Omitted = whole project
  // (fired once from Step 1 -> Step 2). No-duplicate-job guard server-side:
  // if a resegment job is already active for the project, this just returns
  // it instead of creating (and charging for) another.
  resegmentProject: (projectId: string, blockIdx?: number) =>
    request<Job>(
      `/api/projects/${projectId}/jobs/resegment` + (blockIdx !== undefined ? `?block_idx=${blockIdx}` : ""),
      { method: "POST" },
    ),
  // Fast, GPU-free pre-check: has anything on Step 1 changed since the last
  // re-segment? Lets Step 2 auto-trigger a re-segment ONLY when needed.
  // stale_block_ids drives the per-block "resegment before generating?" confirm.
  resegmentNeeded: (projectId: string) =>
    request<{ needed: boolean; stale_block_ids: string[] }>(`/api/projects/${projectId}/resegment/needed`),
  // Does this project already have a resegment job in flight (queued or
  // running)? Lets Step 2 adopt an already-running job's progress instead of
  // ever starting one itself.
  resegmentActive: (projectId: string) =>
    request<{ job_id: string | null }>(`/api/projects/${projectId}/resegment/active`),
  // Subtitle file straight from the blocks' timecodes — no job, the
  // server renders it on the fly.
  exportSubtitlesUrl: (projectId: string, fmt: "srt" | "vtt" = "srt", lang: "translated" | "original" = "translated") =>
    `${API_URL}/api/projects/${projectId}/export/subtitles.${fmt}?lang=${lang}`,
  // Burning subtitles into the picture re-encodes the video, so it is a
  // job (polled like merge), not a download.
  exportSegmentsCsvUrl: (projectId: string) =>
    `${API_URL}/api/projects/${projectId}/export/segments.csv`,
  // TTS workspace: voice the WHOLE script in one take. Not a per-block
  // generate — with nothing to clone from, each separate generation rolls
  // a different voice (measured: 9.4 semitones of drift across ten blocks
  // vs 1.4 in one take).
  // 15s sample of the described voice — cheap to reroll while hunting.
  enqueueAuditionVoice: (projectId: string, body: { seed?: number; text?: string } = {}) =>
    request<Job>(`/api/projects/${projectId}/jobs/audition-voice`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  // Locks in ONE roll, named by its seed — each roll keeps its own file so
  // the designer can offer a history to go back to.
  approveVoiceAnchor: (projectId: string, seed: number) =>
    request<Project>(`/api/projects/${projectId}/voice-anchor?seed=${seed}`, { method: "POST" }),
  clearVoiceAnchor: (projectId: string) =>
    request<Project>(`/api/projects/${projectId}/voice-anchor`, { method: "DELETE" }),
  // The rolls still on disk — the panel's history has to come from here,
  // not from component state, or closing it would orphan playable samples.
  listVoiceRolls: (projectId: string) =>
    request<{ rolls: { seed: number; created_at: number }[] }>(
      `/api/projects/${projectId}/voice-rolls`,
    ),
  voiceSampleUrl: (projectId: string, which: "audition" | "anchor", seed?: number) =>
    `${API_URL}/api/projects/${projectId}/voice-sample?which=${which}` +
    (seed != null ? `&seed=${seed}` : ""),
  enqueueGenerateTts: (projectId: string, body: { seed?: number; pace?: number } = {}) =>
    request<Job>(`/api/projects/${projectId}/jobs/generate-tts`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  // The style is read server-side from the project, so it is NOT a
  // parameter here — `v` only busts the browser cache when the saved style
  // changes (same URL otherwise replays the previous PNG).
  subtitlePreviewUrl: (projectId: string, lang: "translated" | "original", styleVersion: string) =>
    `${API_URL}/api/projects/${projectId}/subtitle-preview.png?lang=${lang}&v=${encodeURIComponent(styleVersion)}`,
  enqueueBurnSubtitles: (
    projectId: string,
    body: { lang?: "translated" | "original" } = {},
  ) =>
    request<Job>(`/api/projects/${projectId}/jobs/burn-subtitles`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  listSubtitlePresets: () => request<SubtitlePreset[]>("/api/subtitle-presets"),
  createSubtitlePreset: (name: string, style: SubtitleStyle) =>
    request<SubtitlePreset>("/api/subtitle-presets", {
      method: "POST",
      body: JSON.stringify({ name, style }),
    }),
  deleteSubtitlePreset: (id: string) =>
    request<void>(`/api/subtitle-presets/${id}`, { method: "DELETE" }),
  downloadSubtitledVideoUrl: (projectId: string) =>
    `${API_URL}/api/projects/${projectId}/download/subtitled-video`,
  exportPromptsUrl: (projectId: string) => `${API_URL}/api/projects/${projectId}/export/prompts.csv`,
  exportTranscriptionUrl: (projectId: string) => `${API_URL}/api/projects/${projectId}/export/transcription.csv`,
  exportCorrectionUrl: (projectId: string) => `${API_URL}/api/projects/${projectId}/export/correction.csv`,
  exportProjectLogUrl: (projectId: string) => `${API_URL}/api/projects/${projectId}/export/project-log.csv`,
  downloadFinalAudioUrl: (projectId: string) => `${API_URL}/api/projects/${projectId}/download/final-audio`,
  downloadCleanedAudioUrl: (projectId: string) => `${API_URL}/api/projects/${projectId}/download/cleaned-audio`,
  downloadFinalStemUrl: (projectId: string, stem: "a" | "b") =>
    `${API_URL}/api/projects/${projectId}/download/final-stem/${stem}`,
  downloadFinalVideoUrl: (projectId: string) => `${API_URL}/api/projects/${projectId}/download/final-video`,
  /** Every deliverable that exists (final audio/video, stems, both CSVs) in
   * one streamed archive — Step 3's "Download everything". Missing pieces are
   * skipped server-side, so the link is valid at any stage of a project. */
  downloadEverythingUrl: (projectId: string) => `${API_URL}/api/projects/${projectId}/download/everything.zip`,
  importCorrection: (
    projectId: string,
    file: File,
    opts: { retranslate: boolean; target_lang: string; translation_method: string; extra_instruction?: string },
  ) => {
    const form = new FormData();
    form.append("file", file);
    const q = new URLSearchParams({
      retranslate: String(opts.retranslate),
      target_lang: opts.target_lang,
      translation_method: opts.translation_method,
      extra_instruction: opts.extra_instruction ?? "",
    });
    return request<Job>(`/api/projects/${projectId}/jobs/import-correction?${q}`, { method: "POST", body: form });
  },

  translateAll: (
    projectId: string,
    body: { target_lang: string; translation_method: string; extra_instruction?: string },
  ) =>
    request<Job>(`/api/projects/${projectId}/jobs/translate-all`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  translateBlock: (
    projectId: string,
    idx: number,
    body: { target_lang: string; translation_method: string; extra_instruction?: string },
  ) =>
    request<Job>(`/api/projects/${projectId}/blocks/${idx}/translate`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  translateUkEn: (text: string) =>
    request<{ translated: string; did_translate: boolean }>("/api/utils/translate-uk-en", {
      method: "POST",
      body: JSON.stringify({ text }),
    }),

  fileUrl: (path: string) => {
    const marker = "/webapp/";
    const i = path.indexOf(marker);
    if (i === -1) return path;
    return `${API_URL}/api/files/${path.slice(i + marker.length)}`;
  },
};
